// frontend/src/components/admin/AIOperationsTab.tsx
//
// Admin AI Operations dashboard tab. Surfaces ai_invocation_log
// aggregations for spend visibility, error rates, per-feature breakdown,
// and recent activity.

import React, { useEffect, useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Alert,
    Stack,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Tooltip,
} from '@mui/material';
import {
    AttachMoney as MoneyIcon,
    BoltOutlined as BoltIcon,
    ErrorOutline as ErrorIcon,
    SpeedOutlined as SpeedIcon,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://3yslsbk5n1.execute-api.us-east-1.amazonaws.com/prod';

interface SummaryShape {
    totalInvocations: number;
    totalCostUsd: number;
    successCount: number;
    errorCount: number;
    disabledCount: number;
    rateLimitedCount: number;
    avgDurationMs: number;
    totalTokensIn: number;
    totalTokensOut: number;
    errorRate: number;
}

interface FeatureShape {
    feature: string;
    invocations: number;
    costUsd: number;
    avgDurationMs: number;
    successCount: number;
    errorCount: number;
    lastInvokedAt: string | null;
}

interface RecentShape {
    invocationId: string;
    feature: string;
    surface: string;
    model: string;
    costUsd: number;
    durationMs: number;
    status: string;
    wasRedirectedOffTopic: boolean;
    errorMessage: string | null;
    invokedAt: string;
}

interface ApiResponse {
    success: boolean;
    window: {
        days: number;
        summary: SummaryShape;
        byFeature: FeatureShape[];
    };
    recent: RecentShape[];
}

const formatCurrency = (n: number) => `$${n.toFixed(4)}`;
const formatPct = (n: number) => `${(n * 100).toFixed(1)}%`;
const formatMs = (n: number) => `${Math.round(n)}ms`;
const formatRelative = (iso: string | null) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString();
};

const statusChipColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    if (status === 'success') return 'success';
    if (status === 'error') return 'error';
    if (status === 'rate_limited') return 'warning';
    return 'default';
};

const AIOperationsTab: React.FC = () => {
    const [days, setDays] = useState(7);
    const [data, setData] = useState<ApiResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedFeature, setSelectedFeature] = useState<string>('all');

    const fetchData = async (windowDays: number) => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('idToken');
            const res = await fetch(`${API_URL}/admin/ai-operations?days=${windowDays}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) {
                throw new Error(`HTTP ${res.status}`);
            }
            const json: ApiResponse = await res.json();
            setData(json);
        } catch (err: any) {
            setError(err?.message || 'Failed to load AI operations data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData(days);
    }, [days]);

    if (loading && !data) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    if (!data) {
        return <Alert severity="info">No data available</Alert>;
    }

    const { summary, byFeature } = data.window;
    const { recent } = data;

    return (
        <Box>
            {/* Header with window selector */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" fontWeight={600}>AI Operations</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Spend, latency, and reliability across all AI-powered features
                    </Typography>
                </Box>
                <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Window</InputLabel>
                    <Select
                        value={days}
                        label="Window"
                        onChange={(e) => setDays(Number(e.target.value))}
                    >
                        <MenuItem value={1}>Last 24h</MenuItem>
                        <MenuItem value={7}>Last 7 days</MenuItem>
                        <MenuItem value={30}>Last 30 days</MenuItem>
                        <MenuItem value={90}>Last 90 days</MenuItem>
                    </Select>
                </FormControl>
            </Stack>

            {/* Summary cards */}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' },
                gap: 2,
                mb: 3,
            }}>
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <MoneyIcon color="primary" />
                            <Typography variant="body2" color="text.secondary">Total Spend</Typography>
                        </Stack>
                        <Typography variant="h4" fontWeight={700}>
                            {formatCurrency(summary.totalCostUsd)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {summary.totalTokensIn.toLocaleString()} in / {summary.totalTokensOut.toLocaleString()} out tokens
                        </Typography>
                    </CardContent>
                </Card>
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <BoltIcon color="primary" />
                            <Typography variant="body2" color="text.secondary">Invocations</Typography>
                        </Stack>
                        <Typography variant="h4" fontWeight={700}>
                            {summary.totalInvocations.toLocaleString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {summary.successCount} success / {summary.errorCount} error
                        </Typography>
                    </CardContent>
                </Card>
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <ErrorIcon color={summary.errorRate > 0.05 ? 'error' : 'primary'} />
                            <Typography variant="body2" color="text.secondary">Error Rate</Typography>
                        </Stack>
                        <Typography variant="h4" fontWeight={700} color={summary.errorRate > 0.05 ? 'error.main' : 'inherit'}>
                            {formatPct(summary.errorRate)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {summary.disabledCount} disabled / {summary.rateLimitedCount} rate-limited
                        </Typography>
                    </CardContent>
                </Card>
                <Card variant="outlined">
                    <CardContent>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                            <SpeedIcon color="primary" />
                            <Typography variant="body2" color="text.secondary">Avg Latency (success)</Typography>
                        </Stack>
                        <Typography variant="h4" fontWeight={700}>
                            {formatMs(summary.avgDurationMs)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Successful invocations only
                        </Typography>
                    </CardContent>
                </Card>
            </Box>

            {/* Per-feature table */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
                        <Typography variant="h6" fontWeight={600}>By Feature</Typography>
                        {byFeature.length > 0 && (
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel id="feature-picker-label">Feature</InputLabel>
                                <Select labelId="feature-picker-label" label="Feature" value={selectedFeature}
                                    onChange={(e) => setSelectedFeature(e.target.value)}>
                                    <MenuItem value="all">All features</MenuItem>
                                    {byFeature.map((f) => (<MenuItem key={f.feature} value={f.feature}>{f.feature}</MenuItem>))}
                                </Select>
                            </FormControl>
                        )}
                    </Box>
                    {byFeature.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No invocations in the selected window.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Feature</TableCell>
                                    <TableCell align="right">Invocations</TableCell>
                                    <TableCell align="right">Spend</TableCell>
                                    <TableCell align="right">Errors</TableCell>
                                    <TableCell align="right">Avg Latency</TableCell>
                                    <TableCell align="right">Last Invoked</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(selectedFeature === "all" ? byFeature : byFeature.filter((f) => f.feature === selectedFeature)).map((f) => (
                                    <TableRow key={f.feature}>
                                        <TableCell>
                                            <Typography variant="body2" fontFamily="monospace">{f.feature}</Typography>
                                        </TableCell>
                                        <TableCell align="right">{f.invocations}</TableCell>
                                        <TableCell align="right">{formatCurrency(f.costUsd)}</TableCell>
                                        <TableCell align="right">
                                            {f.errorCount > 0 ? (
                                                <Chip label={f.errorCount} size="small" color="error" />
                                            ) : (
                                                <Typography variant="body2" color="text.secondary">0</Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">{formatMs(f.avgDurationMs)}</TableCell>
                                        <TableCell align="right">
                                            <Typography variant="caption" color="text.secondary">
                                                {formatRelative(f.lastInvokedAt)}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Recent invocations */}
            <Card variant="outlined">
                <CardContent>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>Recent Invocations</Typography>
                    {recent.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No recent invocations.
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>When</TableCell>
                                    <TableCell>Feature</TableCell>
                                    <TableCell>Surface</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Cost</TableCell>
                                    <TableCell align="right">Latency</TableCell>
                                    <TableCell>Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {recent.map((r) => (
                                    <TableRow key={r.invocationId}>
                                        <TableCell>
                                            <Typography variant="caption">
                                                {formatRelative(r.invokedAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" fontFamily="monospace">
                                                {r.feature}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color="text.secondary">
                                                {r.surface}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={r.status} size="small" color={statusChipColor(r.status)} />
                                        </TableCell>
                                        <TableCell align="right">{formatCurrency(r.costUsd)}</TableCell>
                                        <TableCell align="right">{formatMs(r.durationMs)}</TableCell>
                                        <TableCell>
                                            {r.wasRedirectedOffTopic && (
                                                <Tooltip title="P4 boundary redirect">
                                                    <Chip label="redirected" size="small" variant="outlined" />
                                                </Tooltip>
                                            )}
                                            {r.errorMessage && (
                                                <Tooltip title={r.errorMessage}>
                                                    <Chip label="error detail" size="small" variant="outlined" color="error" />
                                                </Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default AIOperationsTab;
